import { motion } from "framer-motion";
import { HERO } from "@/constants/testIds";
import Spade from "@/components/Spade";
import LightSwitch from "@/components/LightSwitch";
import PortalDoor from "@/components/PortalDoor";
import { ArrowUpRight, Instagram } from "lucide-react";

const WA_URL =
    "https://wa.me/919717622852?text=" +
    encodeURIComponent("Hi, I want to book my $49 Aestherium Media trial week.");
const IG_URL = "https://instagram.com/aestherium.media";

export default function Hero({ site }) {
    return (
        <section
            id="hero"
            data-testid={HERO.root}
            className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 md:pt-32 pb-16"
        >
            {/* Ambient background layers */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-gradient-to-b from-[#8B0A0A] via-[#6B0606] to-transparent" />
                <div className="absolute inset-0 opacity-50 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.15),transparent_60%)]" />
                {/* Watermark spade */}
                <div className="absolute right-[-6%] top-[8%] opacity-[0.08] rotate-[8deg]">
                    <Spade size={520} />
                </div>
                <div className="absolute left-[-4%] bottom-[-10%] opacity-[0.05] -rotate-[12deg]">
                    <Spade size={380} />
                </div>
            </div>

            {/* Portal doors — desktop variant is section-level (viewport-anchored right).
                Two doors sit side by side: real-estate closer to edge, local-business inboard. */}
            <PortalDoor
                href="/real-estate"
                theme="green"
                variant="desktop"
                label="For Real Estate"
                sublabel="Agent or broker?"
                desktopRight="60px"
                ownsSideEffects={true}
            />
            <PortalDoor
                href="/local-business"
                theme="navy"
                variant="desktop"
                label="For Local Business"
                sublabel="Gym, clinic, restaurant?"
                desktopRight="240px"
                ownsSideEffects={false}
            />

            <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 relative">
                <LightSwitch />

                <div data-lit-target>
                    <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-xs md:text-sm uppercase tracking-[0.35em] text-[var(--accent-champagne)]/80 mb-6"
                >
                    Est. 2020 · India · USA · UK
                </motion.p>

                <motion.h1
                    data-testid={HERO.wordmark}
                    initial={{ opacity: 0, y: 24, rotateX: -12 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{ duration: 1, delay: 0.15 }}
                    className="font-display font-bold whitespace-nowrap leading-none tracking-tight text-[var(--text-primary)] wordmark-emboss"
                    style={{
                        fontSize: "clamp(2rem, 9vw, 7rem)",
                        transformOrigin: "50% 100%",
                    }}
                >
                    Aestherium Media
                    <sup className="text-[0.28em] text-[var(--accent-gold)] font-body font-normal tracking-normal align-super ml-1">
                        ®
                    </sup>
                </motion.h1>

                <motion.h2
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.28 }}
                    className="mt-6 md:mt-8 font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-[var(--text-primary)] max-w-4xl"
                >
                    Scale Your Brand. Dominate Your Niche.{" "}
                    <span className="italic text-[var(--accent-champagne)]">
                        Turn Reach Into Revenue.
                    </span>
                </motion.h2>

                <motion.p
                    data-testid={HERO.tagline}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.4 }}
                    className="mt-6 text-lg md:text-xl text-[var(--text-secondary)] max-w-3xl leading-relaxed"
                >
                    Aestherium helps creators, coaches, real estate professionals, and business
                    owners grow organically.{" "}
                    <span className="text-[var(--accent-champagne)]">No paid ads.</span> No
                    shortcuts. A system built to make any brand impossible to ignore.
                </motion.p>

                {/* Stat chips */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.5 }}
                    className="mt-8 flex flex-wrap gap-3"
                >
                    {[
                        `${site?.brands_count || "190+"} Brands Scaled`,
                        "200M+ Reach Delivered in 7 Days",
                        "Zero Ad Spend",
                    ].map((chip, i) => (
                        <span
                            key={i}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-accent)] bg-[rgba(212,175,55,0.06)] text-[var(--accent-champagne)] text-xs md:text-sm uppercase tracking-[0.18em]"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-gold)] soft-pulse" />
                            {chip}
                        </span>
                    ))}
                </motion.div>

                <motion.div
                    data-testid={HERO.stat}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.65 }}
                    className="mt-10 md:mt-14 max-w-3xl"
                >
                    <div className="surface-glass rounded-2xl border border-[var(--border-accent)] p-6 md:p-8 relative overflow-hidden">
                        <p className="font-fraktur text-[var(--accent-gold)] text-sm tracking-[0.2em] mb-3 opacity-80">
                            One client. One week.
                        </p>
                        <p className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-none">
                            <span className="gold-shimmer">
                                {site?.hero_stat_value || "200 Million"}
                            </span>
                        </p>
                        <p className="mt-3 text-[var(--text-primary)] text-lg md:text-xl font-body">
                            {site?.hero_stat_label || "Reach. One Week. Zero Ads."}
                        </p>
                    </div>
                </motion.div>

                <motion.p
                    data-testid={HERO.subheadline}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.9, delay: 0.8 }}
                    className="mt-10 text-[var(--text-secondary)] text-lg md:text-xl max-w-2xl italic font-display"
                >
                    Organic Reach. Real Sales.{" "}
                    <span className="gold-shimmer not-italic font-semibold">Zero Ads.</span>
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85 }}
                    className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                >
                    <a
                        data-testid={HERO.ctaTrial}
                        href={WA_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="group inline-flex items-center gap-3 pl-6 pr-4 py-4 bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm rounded-full hover:bg-[var(--accent-champagne)] transition-all shadow-[0_20px_40px_-15px_rgba(212,175,55,0.6)] hover:translate-y-[-2px]"
                    >
                        Book Your $49 Trial Week
                        <span className="w-9 h-9 rounded-full bg-[var(--brand-dark)] flex items-center justify-center group-hover:rotate-45 transition-transform">
                            <ArrowUpRight size={16} className="text-[var(--accent-gold)]" />
                        </span>
                    </a>
                    <a
                        data-testid={HERO.ctaInstagram}
                        href={IG_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-4 border border-[var(--border-accent)] text-[var(--text-primary)] rounded-full text-sm uppercase tracking-[0.2em] hover:bg-white/5 transition-all"
                    >
                        <Instagram size={16} />
                        DM us on Instagram
                    </a>
                </motion.div>

                {/* Portal doors — mobile variants stack side-by-side below CTAs */}
                <div className="lg:hidden mt-10 mb-2 flex justify-center gap-6">
                    <PortalDoor
                        href="/real-estate"
                        theme="green"
                        variant="mobile"
                        label="For Real Estate"
                        sublabel="Agent or broker?"
                        ownsSideEffects={true}
                    />
                    <PortalDoor
                        href="/local-business"
                        theme="navy"
                        variant="mobile"
                        label="For Local Business"
                        sublabel="Gym, clinic, restaurant?"
                        ownsSideEffects={false}
                    />
                </div>

            {/* Marquee ticker below the fold */}
                <div className="mt-16 md:mt-24 border-y border-[var(--border-subtle)] py-4 overflow-hidden opacity-80">
                    <div className="flex gap-16 whitespace-nowrap marquee-track text-[var(--text-secondary)] uppercase tracking-[0.3em] text-xs font-medium">
                        {Array(2)
                            .fill(0)
                            .map((_, i) => (
                                <div key={i} className="flex gap-16 shrink-0">
                                    <span>Real Estate</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>Hospitality</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>Fitness</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>E-commerce</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>Coaching</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>Marketing</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>Beauty</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>Interior Design</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                    <span>Any Industry</span>
                                    <span className="text-[var(--accent-gold)]">✦</span>
                                </div>
                            ))}
                    </div>
                </div>
                </div>
            </div>
        </section>
    );
}
